import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { ClientListPane } from '@apps/web/components/clients/client_list_pane';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import {
    createAgentChatDestinationResolver,
    normalizeAgentChatSearch,
} from '@apps/web/lib/agent_chat_navigation';

export const clientsSearchSchema = z.object({
    clientId: z.string().optional(),
    chatId: z.string().uuid().optional(),
    chatMode: z.enum(['fullscreen']).optional(),
    slackWebClaim: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

const resolveAgentChatDestination = createAgentChatDestinationResolver();

export async function handleClientsBeforeLoad({
    search,
}: {
    readonly search: z.infer<typeof clientsSearchSchema>;
}): Promise<void> {
    if (
        search.chatId === undefined ||
        search.organizationId === undefined ||
        search.mode === undefined ||
        search.slackWebClaim !== undefined
    ) {
        return;
    }

    const resolution = await resolveAgentChatDestination({
        chatId: search.chatId,
        organizationId: search.organizationId,
        mode: search.mode,
    });
    if (
        resolution.kind === 'stale' ||
        resolution.destination.kind === 'panel'
    ) {
        return;
    }

    const normalizedSearch = normalizeAgentChatSearch(search);
    if (
        normalizedSearch.organizationId === undefined ||
        normalizedSearch.mode === undefined
    ) {
        return;
    }
    throw redirect({
        to: '/agent/runs/$runId',
        params: { runId: resolution.destination.runId },
        search: {
            organizationId: normalizedSearch.organizationId,
            mode: normalizedSearch.mode,
        },
        replace: true,
    });
}

export const Route = createFileRoute('/_app/(main)/clients')({
    component: ClientsPage,
    validateSearch: clientsSearchSchema,
    beforeLoad: handleClientsBeforeLoad,
    head: () => ({
        meta: [
            {
                title: 'Clients | Phoebe',
            },
        ],
    }),
});

function ClientsPage() {
    const {
        clientId,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );

    if (isWaitingForDeepLinkOrganization) {
        return null;
    }

    return <ClientListPane selectedClientId={clientId} />;
}
